import Box from 'common/components/box/Box';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styles from './MaintenancesScreen.module.scss';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { VehicleMaintenancesSearchCriteria } from 'api/vehicleMaintenances/models/VehicleMaintenancesSearchCriteria';
import { DATE_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import dayjs from 'dayjs';
import MaintenanceFiltersScreen, { Filters } from './components/maintenanceFilters/MaintenancesFiltersScreen';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import ListMaintenancesScreen from 'screens/vehicle/components/carMaintenanceTab/components/MaintenanceList/ListMaintenancesScreen';
import { VehicleMaintenanceDto } from 'api/vehicleMaintenances/models/VehicleMaintenanceDto';
import MaintenanceScreen from 'screens/vehicle/components/carMaintenanceTab/components/MaintenanceScreen';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import VatNotice from 'common/components/vatNotice/VatNotice';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';


type RouteProps = {
    itemId: string;
    type: 'new' | 'edit' | 'details';
}

const MaintenancesScreen = () => {
    const { itemId, type } = useParams<RouteProps>();

    const FILTERS_CACHE_KEY = 'vehicle-maintenance-filters-0';
    const isFirstLoad = useRef<boolean>(true);
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    const parseUrlFilters = (): Filters | null => {
        const params = new URLSearchParams(location.search);
        if (Array.from(params.keys()).length === 0) return null;

        const next: Filters = { vehiclesInactive: false };
        const dateFrom = params.get('dateFrom');
        const dateTo = params.get('dateTo');
        const dateField = params.get('dateField') ?? 'executed';

        if (dateFrom || dateTo) {
            if (dateField === 'expected') {
                if (dateFrom) next.startDate = new Date(dateFrom);
                if (dateTo) next.endDate = new Date(dateTo);
            } else {
                if (dateFrom) next.executedStartDate = new Date(dateFrom);
                if (dateTo) next.executedEndDate = new Date(dateTo);
            }
        }

        const vehicleId = params.get('vehicleId');
        if (vehicleId) {
            next.vehicleId = vehicleId;
            const vehicleName = params.get('vehicleName');
            if (vehicleName) next.vehicleName = vehicleName;
        }

        const componentIds = params.getAll('componentIds');
        if (componentIds.length > 0) {
            next.componentIds = componentIds;
            const componentNames = params.getAll('componentNames');
            if (componentNames.length > 0) next.componentNames = componentNames;
        }

        const typeId = params.get('typeId');
        if (typeId) {
            next.typeId = typeId;
            const typeName = params.get('typeName');
            if (typeName) next.typeName = typeName;
        }

        const currentState = params.get('currentState');
        if (currentState) {
            next.currentState = currentState;
            const currentStateName = params.get('currentStateName');
            if (currentStateName) next.currentStateName = currentStateName;
        }

        return next;
    }

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VEHICLE_MAINTENANCES_WRITE']);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [filters, setFilters] = useState<Filters>({ vehiclesInactive: true });
    const [filtersTotal, setFiltersTotal] = useState(1);

    const [criteria, setCriteria] = useState<VehicleMaintenancesSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'vm.expected_date',
        orderBy: 'desc',
        vehiclesInactive: false,
    });

    const updateTotalFilters = (f: Filters) => {
        let total = 0;

        if (f.executedStartDate || f.executedEndDate) {
            total++;
        }
        if (f.startDate || f.endDate) {
            total++;
        }
        if (f.currentState) {
            total++;
        }
        if (f.typeId) {
            total++;
        }
        if (f.supplierId || f.supplierName) {
            total++;
        }
        if (f.vehicleId) {
            total++;
        }
        if (f.responsibleId || f.responsibleName) {
            total++;
        }
        if (f.vehiclesInactive) {
            total++;
        }
        if (f.componentIds && f.componentIds.length > 0) {
            total++;
        }
        if (f.reminderPredicted === true || f.reminderPredicted === false) {
            total++;
        }
        if (f.reminderScheduling === true || f.reminderScheduling === false) {
            total++;
        }
        if (f.segmentId) {
            total++;
        }
        if (f.localIds?.length) {
            total++;
        }

        setFiltersTotal(total);
    }


    const savePageChange = (page: number) => {
        filters.page = page;
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(filters));
    }

    const loadFilters = () => {
        const urlFilters = parseUrlFilters();
        if (urlFilters) {
            // URL params override cached filters when navigating from dashboard widgets.
            // Replace history so a refresh falls back to cached behaviour.
            history.replace('/maintenances');
            onChangeFilters(urlFilters, true);
            return;
        }
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            onChangeFilters(JSON.parse(cachedFilters), true);
        } else {
            onChangeFilters({}, true);
        }
    }

    const onChangeFilters = (_filters: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(_filters);
        
        setFilters({ ..._filters });
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(_filters));

        setCriteria(prev => ({
            ...prev,
            startDate: _filters.startDate,
            endDate: _filters.endDate,
            vehicleId: _filters.vehicleId,
            vehicleName: _filters.vehicleName,
            currentState: _filters.currentState,
            currentStateName: _filters.currentStateName,
            supplierId:  _filters.supplierId,
            supplierName: _filters.supplierName,
            responsibleId: _filters.responsibleId,
            responsibleName: _filters.responsibleName,
            typeId: _filters.typeId,
            typeName: _filters.typeName,
            executedStartDate: _filters.executedStartDate,
            executedEndDate: _filters.executedEndDate,
            vehiclesInactive: _filters.vehiclesInactive,
            orderBy:_filters.orderBy,
            orderColumn:_filters.orderColumn,
            page: (isOnLoad && _filters.page) ? _filters.page : 1,
            componentIds: _filters.componentIds,
            reminderPredicted: _filters.reminderPredicted,
            reminderScheduling: _filters.reminderScheduling,
            localIds: _filters.localIds,
            localOperator: _filters.localOperator,
            segmentId: _filters.segmentId,
        }));

        isFirstLoad.current = false;
    }

    const goToDetails = (id: string) => {
        navigateTo('details', id);
    }

    const goToEdit = (id: string) => {
        if (hasWritePolicy) {
            navigateTo('edit', id);
        } else {
            goToDetails(id);
        }
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            history.push(typeUrl === 'new' ? '/maintenances/new' : `/maintenances/${typeUrl}/${id}`);
        } else {
            history.push('/maintenances');
        }
    }

    useEffect(() => {
        loadFilters();
    }, []);

    const onRowClick = (event: any, item: VehicleMaintenanceDto, isDetails: boolean) => {
        event.preventDefault();
        event.stopPropagation();
        navigateTo(isDetails ? 'details' : 'edit', item.id);
    }

    // Build filter badge items
    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleId && filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('maintenances.filters.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => onChangeFilters({ ...filters, vehicleId: undefined, vehicleName: undefined })
            });
        }

        if (filters.currentState && filters.currentStateName) {
            items.push({
                key: 'state',
                label: t('maintenances.filters.state') as string,
                value: filters.currentStateName,
                onRemove: () => onChangeFilters({ ...filters, currentState: undefined, currentStateName: undefined })
            });
        }

        if (filters.typeId && filters.typeName) {
            items.push({
                key: 'type',
                label: t('maintenances.filters.type') as string,
                value: filters.typeName,
                onRemove: () => onChangeFilters({ ...filters, typeId: undefined, typeName: undefined })
            });
        }

        if (filters.supplierId || filters.supplierName) {
            items.push({
                key: 'supplier',
                label: t('maintenances.filters.supplier') as string,
                value: filters.supplierName || '',
                onRemove: () => onChangeFilters({ ...filters, supplierId: undefined, supplierName: undefined })
            });
        }

        if (filters.responsibleId || filters.responsibleName) {
            items.push({
                key: 'responsible',
                label: t('maintenances.filters.responsible') as string,
                value: filters.responsibleName || '',
                onRemove: () => onChangeFilters({ ...filters, responsibleId: undefined, responsibleName: undefined })
            });
        }

        if (filters.startDate || filters.endDate) {
            const start = filters.startDate ? dayjs(filters.startDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.endDate ? dayjs(filters.endDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'expectedDate',
                label: t('maintenances.filters.expected_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, startDate: undefined, endDate: undefined })
            });
        }

        if (filters.executedStartDate || filters.executedEndDate) {
            const start = filters.executedStartDate ? dayjs(filters.executedStartDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.executedEndDate ? dayjs(filters.executedEndDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'executedDate',
                label: t('maintenances.filters.executed_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, executedStartDate: undefined, executedEndDate: undefined })
            });
        }

        if (filters.componentIds && filters.componentIds.length > 0) {
            items.push({
                key: 'components',
                label: t('maintenances.filters.components') as string,
                value: `${filters.componentIds.length}`,
                onRemove: () => onChangeFilters({ ...filters, componentIds: undefined })
            });
        }

        if (filters.vehiclesInactive) {
            items.push({
                key: 'vehiclesInactive',
                label: t('maintenances.filters.active_vehicles') as string,
                value: t('common.yes') as string,
                onRemove: () => onChangeFilters({ ...filters, vehiclesInactive: false })
            });
        }

        if (filters.reminderPredicted === true || filters.reminderPredicted === false) {
            items.push({
                key: 'reminderPredicted',
                label: t('maintenances.list.reminder_predicted') as string,
                value: filters.reminderPredictedName || (filters.reminderPredicted ? t('common.yes') : t('common.no')) as string,
                onRemove: () => onChangeFilters({ ...filters, reminderPredicted: undefined, reminderPredictedName: undefined })
            });
        }

        if (filters.reminderScheduling === true || filters.reminderScheduling === false) {
            items.push({
                key: 'reminderScheduling',
                label: t('maintenances.list.reminder_scheduling') as string,
                value: filters.reminderSchedulingName || (filters.reminderScheduling ? t('common.yes') : t('common.no')) as string,
                onRemove: () => onChangeFilters({ ...filters, reminderScheduling: undefined, reminderSchedulingName: undefined })
            });
        }

        if (filters.segmentId && filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => onChangeFilters({ ...filters, segmentId: undefined, segmentName: undefined })
            });
        }

        if (filters.localIds?.length && filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => onChangeFilters({ ...filters, localIds: [], localNames: [] })
            });
        }

        return items;
    }, [filters, t]);

    const getHeaderTitle = () => {
        if (!type) return t('maintenances.title');
        if (type === 'new') return t('maintenances.new');
        if (type === 'details') return t('maintenances.details');
        if (type === 'edit') return t('maintenances.edit');
        return t('maintenances.title');
    };

    return (
        <ScreenTitle title={t('maintenances.title')}>
            <CompactPageLayout
                title={getHeaderTitle() as string}
                onBack={type ? () => history.goBack() : undefined}
                contentAreaClassName={styles.contentArea}
                middleContent={!type ? <FilterBadgesOverflow items={filterItems} align="right" /> : undefined}
                headerButtons={
                    !type ? (
                        <>
                            <Popover
                                contentContainerClassName={styles.filtersPopoverContainer}
                                containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                onClickOutside={() => updateTotalFilters(filters)}
                                content={setIsPopoverOpen => <MaintenanceFiltersScreen
                                    genericFilter={true}
                                    filters={filters}
                                    onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                                    onChange={_filters => updateTotalFilters(_filters)}
                                />}
                            >
                                {(isPopoverOpen, setIsPopoverOpen) => (
                                    <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                        {filtersTotal > 0 &&
                                            <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                        }
                                    </ScreenHeaderButton>
                                )}
                            </Popover>
                            <ExportListingButton exportRef={exportListingRef} />
                            {hasWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                        </>
                    ) : undefined
                }
            >
                {/* LIST */}
                {!type && !isFirstLoad.current && (
                    <div className={compactPageLayoutStyles.countersSection}>
                        <VatNotice />
                    </div>
                )}
                {!type && !isFirstLoad.current &&
                    <ListMaintenancesScreen
                        editable={true}
                        criteria={criteria}
                        createNewMaintenance={() => null}
                        onClickItem={(item: VehicleMaintenanceDto, isDetails: boolean, event: any) => onRowClick(event, item, isDetails)}
                        exportRef={exportListingRef}
                    />
                }
                {/* DETAILS */}
                {type && <Box className={`${styles.box}`}>
                    <MaintenanceScreen
                        editable={true}
                        item={{ id: itemId } as VehicleMaintenanceDto}
                        isDetails={type === 'details'}
                        onItemDetails={isDetails => isDetails ? goToDetails(itemId) : goToEdit(itemId)}
                        onSubmitDone={() => history.goBack()}
                        onBackList={() => history.goBack()}
                        titleHeader={undefined}
                    />
                </Box>}
            </CompactPageLayout>
        </ScreenTitle>
    );
}

export default MaintenancesScreen;
